import React, { useEffect, useMemo, useState } from "react";
// ANT DESIGN COMPONENTS
import { Table, Tag, Button } from "antd";
import { EditOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import type { MapMetaData } from "pages/mapViewManagement/mapSelector/types";
import GETJwtToken from "../../../redux/authentication/authentication";
import { useLazyGetMapViewQuery } from "../../../redux/mapView/mapViewAPI";

// TYPES AND DATA
import {
  COMPANY_COLUMN_WIDTH,
  DEFAULT_PAGE_SIZE,
  MAP_EDITOR_ROUTE_PREFIX,
  MAP_NAME_COLUMN_WIDTH,
  PAGE_SIZE_OPTIONS,
  TABLE_ELLIPSIS_CLASS,
  TABLE_EMPTY_ERROR_MESSAGE,
  TABLE_NO_ITEMS_MESSAGE,
  TABLE_SCROLL_X,
} from "./constants";
// AUTHORIZATION
import validatePermission, { Permission } from "../../../utils/validatePermissions";
import UnauthorizedPage from "../../unauthorized/UnauthorizedPage";
import { getTableRowClass } from "../../../utils/utils";
// COMPONENTS
import TableNoDataBox from "../../components/table/TableNoDataBox";
import CreateMapModal from "./CreateMapModal";
import { RootState } from "../../../app/store";
import formatUuidT from "../../../flatbuffers/display/uuid";

function MapSelectorPage() {
  // ************************************************ */
  // STATE ****************************************** */
  const [openCreateMapModal, setOpenCreateMapModal] = useState(false);
  const [editingMap, setEditingMap] = useState<MapMetaData | null>(null);

  // ************************************************ */
  // HOOKS ****************************************** */
  const navigate = useNavigate();
  const [
    triggerGetMapView,
    { data: dataGetMapView, isLoading: isLoadingGetMapView, isFetching: isFetchingGetMapView },
  ] = useLazyGetMapViewQuery();
  const { theme, globalOrganizationName, globalOrganizationId, userLogged } = useSelector(
    (state: RootState) => state.home,
  );
  const { permissions } = useSelector((state: RootState) => state.user);

  const resolvedOrganizationName =
    globalOrganizationName || userLogged.org?.name || userLogged.org?.companyName || "";
  const resolvedOrganizationId = (globalOrganizationId ||
    userLogged.organizationId ||
    userLogged.org?.id ||
    "") as string;

  useEffect(() => {
    const getInitialData = async () => {
      if (!resolvedOrganizationId) {
        return;
      }

      const token = await GETJwtToken();
      triggerGetMapView({
        token,
        body: {
          org_id: resolvedOrganizationId,
        },
      });
    };

    getInitialData();
  }, [resolvedOrganizationId, triggerGetMapView]);

  const tableRows: MapMetaData[] = useMemo(() => {
    const apiRows: any[] = Array.isArray(dataGetMapView?.data) ? dataGetMapView.data : [];

    return apiRows.map(
      (item: any) =>
        ({
          id: formatUuidT(item?.id),
          mapName: item?.name || item?.mapName || "",
          company: item?.company || resolvedOrganizationName || "--",
          mapImgUrl: item?.map_img_url || item?.mapImgUrl || item?.offline_url || "",
          hasDisplayView: Boolean(item?.has_display_view ?? item?.hasDisplayView ?? false),
          isMain: Boolean(item?.is_main ?? item?.isMain ?? item?.main ?? false),
          main: Boolean(item?.main ?? item?.is_main ?? item?.isMain ?? false),
          waypoints: item?.waypoints || [],
          groups: item?.group_waypoints || item?.groups || [],
          banks: item?.bank_waypoints || item?.banks || [],
        }) as MapMetaData,
    );
  }, [dataGetMapView, resolvedOrganizationName]);

  const totalRows = tableRows.length;

  const handleRefreshTable = async () => {
    const token = await GETJwtToken();
    triggerGetMapView({
      token,
      body: {
        org_id: resolvedOrganizationId,
      },
    });
  };

  return (
    <div className={`layout__contentPage__${theme}`}>
      {validatePermission(Permission.ViewEdgeManagementMap, permissions) ? (
        <>
          <div style={{ marginBottom: "16px" }}>
            <Button
              type="primary"
              onClick={() => {
                setEditingMap(null);
                setOpenCreateMapModal(true);
              }}
            >
              Create Map
            </Button>
          </div>
          <Table
            locale={{
              emptyText: (
                <TableNoDataBox
                  isError={false}
                  errorMessage={TABLE_EMPTY_ERROR_MESSAGE}
                  noItemsFound={TABLE_NO_ITEMS_MESSAGE}
                />
              ),
            }}
            rowClassName={(record, index) => getTableRowClass(index, theme)}
            className="customTable"
            loading={isLoadingGetMapView || isFetchingGetMapView}
            dataSource={tableRows}
            rowKey={(record: MapMetaData) => record.id}
            onRow={(record: MapMetaData) => ({
              onClick: () => {
                navigate(`${MAP_EDITOR_ROUTE_PREFIX}/${record.id}`);
              },
            })}
            size="small"
            pagination={{
              defaultPageSize: DEFAULT_PAGE_SIZE,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
            }}
            scroll={{ x: TABLE_SCROLL_X }}
            footer={() => (
              <div className="generalStyles__flexEnd">
                <Tag>{totalRows} Map Views</Tag>
              </div>
            )}
          >
            <Table.Column<MapMetaData>
              title="Map Name"
              dataIndex="mapName"
              key="mapName"
              width={MAP_NAME_COLUMN_WIDTH}
              render={(text: string) => <div className={TABLE_ELLIPSIS_CLASS}>{text}</div>}
            />
            <Table.Column<MapMetaData>
              title="Company"
              dataIndex="company"
              key="company"
              width={COMPANY_COLUMN_WIDTH}
              render={(text: string) => <div className={TABLE_ELLIPSIS_CLASS}>{text}</div>}
            />
            <Table.Column<MapMetaData>
              title="Display View"
              dataIndex="hasDisplayView"
              key="hasDisplayView"
              width={110}
              render={(_, record) =>
                record.hasDisplayView ? (
                  <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 18 }} />
                ) : (
                  <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 18 }} />
                )
              }
            />
            <Table.Column<MapMetaData>
              title="Edit"
              key="edit"
              width={80}
              render={(_, record) => (
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={(event) => {
                    event.stopPropagation();
                    setEditingMap(record);
                    setOpenCreateMapModal(true);
                  }}
                />
              )}
            />
          </Table>
          <CreateMapModal
            open={openCreateMapModal}
            editingMap={editingMap}
            onClose={() => {
              setEditingMap(null);
              setOpenCreateMapModal(false);
            }}
            organizationId={resolvedOrganizationId}
            onMapCreated={handleRefreshTable}
          />
        </>
      ) : (
        <UnauthorizedPage />
      )}
    </div>
  );
}

export default MapSelectorPage;
